import { useEffect, useRef, useCallback, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

const WARNING_MS = 30 * 1000; // Show countdown for last 30 seconds
const THROTTLE_MS = 30 * 1000; // Only reschedule timers every 30s to avoid churn

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'keyup', 'touchstart', 'touchmove', 'scroll', 'wheel'];

export function useAdminSessionTimeout() {
  const { user, signOut, adminUser } = useAdminAuth();

  // Use per-user setting from admin_users.session_timeout_minutes (default 5)
  const timeoutMs = (adminUser?.sessionTimeoutMinutes ?? 5) * 60 * 1000;

  const timeoutMsRef = useRef(timeoutMs);
  timeoutMsRef.current = timeoutMs;

  const lastActivityRef = useRef(Date.now());
  const lastResetRef = useRef(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const showCountdownRef = useRef(false);

  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const doSignOut = useCallback(() => {
    console.log('[AdminSessionTimeout] Inactivity timeout — signing out');
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);
    signOutRef.current();
  }, [clearTimers]);

  const scheduleTimers = useCallback((ms: number) => {
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);

    // Guard: if timeout is less than the warning window, show warning immediately
    const warningDelay = Math.max(0, ms - WARNING_MS);

    warningRef.current = setTimeout(() => {
      setShowCountdown(true);
      showCountdownRef.current = true;
      const secs = Math.ceil(Math.min(ms, WARNING_MS) / 1000);
      setCountdownSeconds(secs);
      countdownRef.current = setInterval(() => {
        setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, warningDelay);

    timerRef.current = setTimeout(() => {
      // Final check: if user was active since timer was set, don't log out
      const elapsed = Date.now() - lastActivityRef.current;
      const currentMs = timeoutMsRef.current;
      if (elapsed < currentMs - 1000) {
        scheduleTimers(currentMs - elapsed);
        return;
      }
      doSignOut();
    }, ms);
  }, [clearTimers, doSignOut]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(timeoutMsRef.current);
  }, [scheduleTimers]);

  useEffect(() => {
    if (!user) {
      clearTimers();
      setShowCountdown(false);
      showCountdownRef.current = false;
      setCountdownSeconds(0);
      return;
    }

    // User is present — start inactivity timers
    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(timeoutMsRef.current);

    const handler = () => {
      lastActivityRef.current = Date.now();
      // If countdown is showing, any activity dismisses it and resets
      if (showCountdownRef.current) {
        lastResetRef.current = Date.now();
        scheduleTimers(timeoutMsRef.current);
        return;
      }
      // Throttle: only reschedule every THROTTLE_MS to avoid timer churn
      if (Date.now() - lastResetRef.current > THROTTLE_MS) {
        lastResetRef.current = Date.now();
        scheduleTimers(timeoutMsRef.current);
      }
    };

    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, handler, { passive: true }));

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        const currentTimeout = timeoutMsRef.current;
        if (elapsed >= currentTimeout) {
          // Timeout already passed while tab was hidden — sign out immediately
          clearTimers();
          setShowCountdown(false);
          showCountdownRef.current = false;
          setCountdownSeconds(0);
          signOutRef.current();
        } else if (elapsed >= currentTimeout - WARNING_MS) {
          // In the warning window — show countdown with accurate remaining time
          const remaining = currentTimeout - elapsed;
          clearTimers();
          setShowCountdown(true);
          showCountdownRef.current = true;
          setCountdownSeconds(Math.ceil(remaining / 1000));
          countdownRef.current = setInterval(() => {
            setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
          }, 1000);
          timerRef.current = setTimeout(() => {
            clearTimers();
            setShowCountdown(false);
            showCountdownRef.current = false;
            signOutRef.current();
          }, remaining);
        } else {
          // Still has time — reschedule from elapsed
          lastResetRef.current = Date.now();
          scheduleTimers(currentTimeout - elapsed);
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, handler));
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { showCountdown, countdownSeconds, resetTimer };
}
